/**
 * PetalsManagerPage
 *
 * Main page for managing petals (stories, case studies, talking points).
 * Left: List of petals grouped by flower type.
 * Right: Form for creating/editing petals.
 */

import { useState } from 'react';
import {
  PetalDto,
  CreatePetalDto,
  useGetPetalsQuery,
  useCreatePetalMutation,
  useUpdatePetalMutation,
  useDeletePetalMutation,
} from '../store/api/storyPrepApi';
import { PetalsList } from '../components/petals/PetalsList';
import { PetalForm } from '../components/petals/PetalForm';

export function PetalsManagerPage() {
  // Selected petal for editing (null = create mode)
  const [selectedPetal, setSelectedPetal] = useState<PetalDto | null>(null);
  const [showForm, setShowForm] = useState(false);

  // RTK Query hooks
  const { data: petals = [], isLoading, error } = useGetPetalsQuery();
  const [createPetal, { isLoading: isCreating }] = useCreatePetalMutation();
  const [updatePetal, { isLoading: isUpdating }] = useUpdatePetalMutation();
  const [deletePetal] = useDeletePetalMutation();

  // Handle petal selection
  const handleSelectPetal = (petal: PetalDto) => {
    setSelectedPetal(petal);
    setShowForm(true);
  };

  // Handle create new
  const handleCreateNew = () => {
    setSelectedPetal(null);
    setShowForm(true);
  };

  // Handle form submission
  const handleSubmit = async (data: CreatePetalDto) => {
    try {
      if (selectedPetal) {
        await updatePetal({ id: selectedPetal.id, data }).unwrap();
      } else {
        await createPetal(data).unwrap();
      }
      setShowForm(false);
      setSelectedPetal(null);
    } catch (err) {
      console.error('Failed to save petal:', err);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setShowForm(false);
    setSelectedPetal(null);
  };

  // Handle delete
  const handleDelete = async (petalId: string) => {
    try {
      await deletePetal(petalId).unwrap();
      if (selectedPetal?.id === petalId) {
        setShowForm(false);
        setSelectedPetal(null);
      }
    } catch (err) {
      console.error('Failed to delete petal:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Petals Manager</h1>
          <p className="mt-1 text-gray-500">
            Build your library of stories, case studies, and talking points
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Petal
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          Failed to load petals. Please try again.
        </div>
      )}

      {/* Main content - two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Petals list */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-body">
              <PetalsList
                petals={petals}
                selectedPetalId={selectedPetal?.id}
                onSelect={handleSelectPetal}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Right column - Form */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">
                {selectedPetal ? 'Edit Petal' : 'Create New Petal'}
              </h2>
              <p className="text-sm text-gray-500">
                {selectedPetal
                  ? 'Update your petal details'
                  : 'Add a new story, case study, or talking point to your library'}
              </p>
            </div>
            <div className="card-body">
              {showForm ? (
                <PetalForm
                  petal={selectedPetal}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isCreating || isUpdating}
                />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-5xl mb-4">🌸</div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Ready to add a new petal?
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    Click "New Petal" or select an existing one to edit
                  </p>
                  <button
                    onClick={handleCreateNew}
                    className="inline-flex items-center gap-2 px-4 py-2 text-primary-600 bg-primary-50 font-medium rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Your First Petal
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tips card */}
          <div className="card mt-6">
            <div className="card-header">
              <h3 className="font-semibold text-gray-900">Tips for Great Petals</h3>
            </div>
            <div className="card-body">
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-primary-600">1.</span>
                  <span>
                    <strong>Stories</strong> should have a clear beginning, challenge, and resolution.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600">2.</span>
                  <span>
                    <strong>Case studies</strong> work best with specific metrics and outcomes.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600">3.</span>
                  <span>
                    <strong>Irrefutable logic</strong> points should be clear and memorable.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary-600">4.</span>
                  <span>
                    Match petals to the right <strong>flower</strong> for easy recall during conversations.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PetalsManagerPage;
